import React, { useContext, useEffect, useState } from 'react'
import moment from 'moment'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Paper from '@mui/material/Paper'
import Radio from '@mui/material/Radio'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import AirportShuttleIcon from '@mui/icons-material/AirportShuttle'
import { BookingContext } from './NewBooking'
import { SectionHeader } from './NewBooking'
import API from '../../../services/API'

const getAnchor = (formData) =>
  formData?.pickup?.when?.exactTime ||
  formData?.pickup?.when?.patientReadyFrom ||
  formData?.dropoff?.when?.targetArrivalTime ||
  null

const crewLabel = (duty) =>
  [
    duty?.vehicle?.callSign,
    duty?.crew?.length ? duty.crew.join(' & ') : null,
  ]
    .filter(Boolean)
    .join(' — ') || 'Crew'

/**
 * Hospital-portal only: when the organisation has a dedicated crew covering
 * the requested time, the journey (any type) books straight onto it
 * (auto-approved). One eligible crew auto-selects; several offer a choice;
 * none shows a warning that the request will go for review instead.
 *
 * `quietWhenNone` hides the section unless a dedicated crew is actually on
 * duty — used for every journey type except Discharge, so hospitals without
 * a dedicated crew aren't warned about one on every booking.
 */
const DischargeCrewSection = ({ quietWhenNone = false }) => {
  const { formData, handleChange } = useContext(BookingContext)
  const [duties, setDuties] = useState(null)
  const [loading, setLoading] = useState(false)

  const anchor = getAnchor(formData)
  const anchorISO = anchor ? new Date(anchor).toISOString() : null

  useEffect(() => {
    if (!anchorISO) {
      setDuties(null)
      handleChange('selectedDuty', null)
      return
    }
    let stale = false
    setLoading(true)
    API.get(`/pts/portal/discharge-duties?when=${encodeURIComponent(anchorISO)}`)
      .then((res) => {
        if (stale) return
        if (res.status === 200) {
          const options = res.data || []
          setDuties(options)
          if (options.length === 1) {
            handleChange('selectedDuty', options[0]._id)
          } else if (
            !options.some(
              (duty) => String(duty._id) === String(formData?.selectedDuty),
            )
          ) {
            handleChange('selectedDuty', null)
          }
        } else {
          setDuties([])
          handleChange('selectedDuty', null)
        }
      })
      .catch((e) => {
        console.error(e)
        if (!stale) setDuties([])
      })
      .finally(() => !stale && setLoading(false))
    return () => {
      stale = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorISO])

  // Leaving the booking flow (this section unmounting) must not leave a
  // stale crew selection on the form
  useEffect(
    () => () => {
      handleChange('selectedDuty', null)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  if (quietWhenNone && !duties?.length) return null

  if (!anchorISO) {
    return (
      <Box>
        <SectionHeader
          icon={<AirportShuttleIcon fontSize="small" />}
          title="Dedicated Crew"
        />
        <Alert severity="info">
          Set the pickup time above to check your dedicated crew's
          availability.
        </Alert>
      </Box>
    )
  }

  return (
    <Box>
      <SectionHeader
        icon={<AirportShuttleIcon fontSize="small" />}
        title="Dedicated Crew"
      />
      {loading && !duties && (
        <Typography color="text.secondary">Checking crew cover…</Typography>
      )}
      {duties && duties.length === 0 && (
        <Alert severity="warning">
          <b>
            Your organisation has no dedicated crew on duty at{' '}
            {moment(anchor).format('HH:mm [on] ddd D MMM')}.
          </b>{' '}
          You can still submit this request — it will be sent to the transport
          provider for review — but please contact them to confirm cover for
          this time.
        </Alert>
      )}
      {duties && duties.length === 1 && (
        <Alert severity="success" icon={<AirportShuttleIcon />}>
          This journey will be booked directly with your dedicated crew{' '}
          <b>{crewLabel(duties[0])}</b> (on duty{' '}
          {moment(duties[0].start).format('HH:mm')}–
          {moment(duties[0].end).format('HH:mm')}) and approved automatically.
          {duties[0].activeJourneyCount > 0 && (
            <>
              {' '}
              They already have {duties[0].activeJourneyCount} journey
              {duties[0].activeJourneyCount === 1 ? '' : 's'} booked, so
              collection times may be staggered.
            </>
          )}
        </Alert>
      )}
      {duties && duties.length > 1 && (
        <Stack spacing={1}>
          <Typography color="text.secondary" variant="body2">
            More than one dedicated crew covers this time — choose who should
            take this journey. It will be approved automatically.
          </Typography>
          {duties.map((duty) => {
            const selected =
              String(formData?.selectedDuty || '') === String(duty._id)
            return (
              <Paper
                key={duty._id}
                variant="outlined"
                onClick={() => handleChange('selectedDuty', duty._id)}
                sx={{
                  p: 1.5,
                  cursor: 'pointer',
                  borderColor: selected ? 'primary.main' : 'divider',
                  borderWidth: selected ? 2 : 1,
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Radio checked={selected} size="small" />
                  <Box flex={1} minWidth={0}>
                    <Typography fontWeight={800} noWrap>
                      {crewLabel(duty)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      On duty {moment(duty.start).format('HH:mm')}–
                      {moment(duty.end).format('HH:mm')}
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    label={
                      duty.activeJourneyCount
                        ? `${duty.activeJourneyCount} booked`
                        : 'Free'
                    }
                    color={duty.activeJourneyCount ? 'warning' : 'success'}
                    variant="outlined"
                    sx={{ borderRadius: 1, fontWeight: 700 }}
                  />
                </Stack>
              </Paper>
            )
          })}
          {!formData?.selectedDuty && (
            <Alert severity="info">
              If you don't choose a crew, the request will be sent for review
              instead of being booked directly.
            </Alert>
          )}
        </Stack>
      )}
    </Box>
  )
}

export default DischargeCrewSection
